/**
 * Zone UI utilities — colors, durations, multipliers.
 *
 * Constants must match on-chain config:
 *   minDuration = 180 (3 min)
 *   maxDuration = 259200 (3 days)
 *   maxZone = 29
 *   zoneRewardBonusBps = 90000
 */

const MIN_DURATION = 180;
const MAX_DURATION = 259_200;
const MAX_ZONE = 29;
const BPS = 10_000;
const ZONE_REWARD_BONUS_BPS = 90_000;

// ---- Zone color gradient (green → yellow → orange → red) ----

/**
 * Returns an HSL color string for the zone (0-29).
 * Green (hue ~130) at zone 0 → Red (hue ~0) at zone 29.
 */
export function getZoneColor(zone: number): string {
	const t = Math.min(1, Math.max(0, zone / MAX_ZONE));
	const hue = 130 * (1 - t); // 130 (green) → 0 (red)
	const sat = 70 + t * 10; // 70% → 80%
	const light = 55 - t * 10; // 55% → 45%
	return `hsl(${hue}, ${sat}%, ${light}%)`;
}

/**
 * Returns [r, g, b] in 0-1 range for Three.js vertex colors.
 */
export function getZoneColorRGB(zone: number): [number, number, number] {
	const t = Math.min(1, Math.max(0, zone / MAX_ZONE));
	const hue = 130 * (1 - t);
	const sat = (70 + t * 10) / 100;
	const light = (55 - t * 10) / 100;
	return hslToRgb(hue / 360, sat, light);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
	const a = s * Math.min(l, 1 - l);
	const f = (n: number) => {
		const k = (n + h * 12) % 12;
		return l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
	};
	return [f(0), f(8), f(4)];
}

// ---- Duration ----

/** Farming duration in seconds for a given zone. Matches on-chain FarmingMath.calculateDuration. */
export function getZoneDuration(zone: number): number {
	return MIN_DURATION + Math.floor(((MAX_DURATION - MIN_DURATION) * zone) / MAX_ZONE);
}

/** Human-readable duration string. */
export function formatDuration(seconds: number): string {
	if (seconds < 60) return `${seconds}s`;
	if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
	if (seconds < 86_400) {
		const h = Math.floor(seconds / 3600);
		const m = Math.floor((seconds % 3600) / 60);
		return m > 0 ? `${h}h ${m}m` : `${h}h`;
	}
	const d = Math.floor(seconds / 86_400);
	const h = Math.floor((seconds % 86_400) / 3600);
	return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

/** Returns formatted zone duration string (e.g. "3m", "18h", "3d"). */
export function formatZoneDuration(zone: number): string {
	return formatDuration(getZoneDuration(zone));
}

// ---- Reward multiplier ----

/** Reward multiplier for the zone as a human-readable number (e.g. 1.0, 5.5, 10.0). */
export function getZoneMultiplier(zone: number): number {
	return 1 + (ZONE_REWARD_BONUS_BPS * zone) / (MAX_ZONE * BPS);
}

/** Formatted multiplier string (e.g. "1.0x", "5.5x", "10.0x"). */
export function formatZoneMultiplier(zone: number): string {
	return `${getZoneMultiplier(zone).toFixed(1)}x`;
}

// ---- Zone badge label ----

/** Short label for the zone (e.g. "Zone 0", "Zone 15", "Zone 29"). */
export function getZoneLabel(zone: number): string {
	return `Zone ${zone}`;
}

// ---- Countdown helper ----

/** Returns remaining seconds for a farm session, or 0 if ready. */
export function getRemainingSeconds(startedAt: number, duration: number): number {
	const now = Math.floor(Date.now() / 1000);
	const endAt = startedAt + duration;
	return Math.max(0, endAt - now);
}

/** Formats a countdown (e.g. "2d 14h 32m 15s" or "Ready"). */
export function formatCountdown(remainingSeconds: number): string {
	if (remainingSeconds <= 0) return "Ready";
	const d = Math.floor(remainingSeconds / 86_400);
	const h = Math.floor((remainingSeconds % 86_400) / 3600);
	const m = Math.floor((remainingSeconds % 3600) / 60);
	const s = remainingSeconds % 60;
	const parts: string[] = [];
	if (d > 0) parts.push(`${d}d`);
	if (h > 0) parts.push(`${h}h`);
	if (m > 0) parts.push(`${m}m`);
	if (s > 0 && d === 0) parts.push(`${s}s`);
	return parts.join(" ") || "Ready";
}
